import { isFeatureEnabled } from 'owa-feature-flags';
import folderStore from 'owa-folders';
import {
    lazyGetInboxRulesAttachedToFolder,
    lazySetAttachedInboxRulesDialogStateMutator,
} from 'owa-inbox-rules-option';
import isFolderUnderDeletedItemsFolder from 'owa-mail-store/lib/utils/isFolderUnderDeletedItemsFolder';
import type { MailboxInfo } from 'owa-client-types';
import doesFolderIdEqualName from 'owa-session-store/lib/utils/doesFolderIdEqualName';
import { PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID } from 'owa-folders-constants';

/*
 * This function checks if the dialog for attached inbox rules should be shown.
 * It checks if the feature is enabled, if the folder is being moved to Deleted Items,
 * and if there are any inbox rules attached to the source folder.
 * If all conditions are met, it sets the state to show the dialog.
 *
 */
export default async function shouldShowAttachedInboxRulesDialog(
    onMoveToFolderCallback: () => void,
    moveToFolderId: string,
    sourceFolderId: string,
    sourceFolderMailboxInfo: MailboxInfo,
    onDismiss?: () => void
): Promise<boolean> {
    if (!isFeatureEnabled('tri-delete-folder-rules-dialog')) {
        return false;
    }
    const isDndCase = !onDismiss;
    const folder = folderStore().folderTable.get(moveToFolderId);
    const sourceFolder = folderStore().folderTable.get(sourceFolderId);
    const isValidDeleteCase =
        isDndCase && folder
            ? isFolderUnderDeletedItemsFolder(folder, PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID)
            : doesFolderIdEqualName(moveToFolderId, PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID);

    if (isValidDeleteCase) {
        // If the folder is being moved to under Deleted Items folder,
        // we need to check if there are any inbox rules attached to it
        // and show the dialog to delete the folder if there are any rules attached.
        const attachedRules = await lazyGetInboxRulesAttachedToFolder.importAndExecute(
            sourceFolderId,
            sourceFolderMailboxInfo
        );

        if (attachedRules.length > 0 && sourceFolder) {
            onDismiss?.(); // MoveToFolderDialog case: dismiss before showing the rules dialog
            await lazySetAttachedInboxRulesDialogStateMutator.importAndExecute(
                true /* isShown */,
                sourceFolderId,
                sourceFolderMailboxInfo,
                attachedRules,
                onMoveToFolderCallback /* onDeleteFolderCallback */,
                sourceFolder /* folder */
            );
            return true;
        }
    }
    return false;
}
